"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { PhoneCall, CircleQuestionMark as HelpCircle } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const faqs = [{
  question: "How often should I maintain my HVAC system?",
  answer: "Twice yearly is typical — spring AC check and fall furnace check. We also recommend replacing your air filter every 1–3 months depending on usage and household factors like pets."
}, {
  question: "What's included in a tune-up?",
  answer: "A standard tune-up typically includes filter replacement, coil inspection, refrigerant check, electrical connection inspection, and a system efficiency test."
}, {
  question: "How much does a new HVAC system cost?",
  answer: "Pricing depends on the system size, efficiency rating, and your home's specific requirements. Your pro gives a quote before work starts."
}, {
  question: "Do pros offer urgent or after-hours service?",
  answer: "Availability varies by pro. Ask your pro about availability when requesting service."
}, {
  question: "What's the warranty on new installations?",
  answer: "Warranty terms are set by the pro and the equipment manufacturer — confirm warranty terms before work begins."
}, {
  question: "How long does a system installation take?",
  answer: "A standard system replacement typically takes 1–3 days depending on the complexity of the job, ductwork modifications required, and the specific equipment being installed."
}, {
  question: "Who performs the HVAC work?",
  answer: "Handy Heroes connects you with local independent HVAC professionals who join through network onboarding."
}];
export default function HvacFaq() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const faqReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const ctaReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="hvacmaintenancefaq" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerReveal.ref} className={`mb-12 text-center transition-all duration-700 ease-out ${headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Badge variant="secondary" className="mb-4">
            <HelpCircle className="mr-1 size-3" />
            HVAC Support
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Frequently Asked Questions
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            Find answers to common questions about HVAC maintenance, repair,
            and installation in Atlanta.
          </p>
        </div>

        {}
        <div ref={faqReveal.ref} className={`mx-auto max-w-3xl rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm transition-all duration-700 delay-100 ease-out ${faqReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i}>
                <AccordionTrigger className="text-base sm:text-lg font-semibold text-card-foreground hover:text-primary">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {}
        <div ref={ctaReveal.ref} className={`mt-12 text-center transition-all duration-700 delay-200 ease-out ${ctaReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <p className="text-muted-foreground mb-6">
            {"Still have questions or need assistance?"}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="#bookingrequest">Request a Quote</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <a href="tel:+14708479076" className="inline-flex items-center">
                <PhoneCall className="mr-2 size-4" />
                {"(470) 847-9076"}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}